import React, { useState, useEffect } from 'react';
import './ManageTenders.css';
import apiService from './api';

function ManageTenders() {
  const [tenders, setTenders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingTenders, setDeletingTenders] = useState(new Set());

  // Load tenders when component mounts
  useEffect(() => {
    loadTenders();
  }, []);

  const loadTenders = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.listFirebaseTenders(); // API endpoint now reads from Google Sheets

      if (response.success) {
        setTenders(response.tenders);
        setError('');
      } else {
        setError(response.message || 'Failed to load tenders');
      }
    } catch (error) {
      setError('Connection error: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTender = async (tenderId, tenderTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${tenderTitle}"?`)) {
      return;
    }

    setDeletingTenders(prev => new Set(prev).add(tenderId));

    try {
      const response = await apiService.deleteTender(tenderId);

      if (response.success) {
        // Remove from local state
        setTenders(prev => prev.filter(t => t.tender_id !== tenderId));
        alert(`✅ Tender "${tenderTitle}" deleted successfully!`);
      } else {
        alert(`❌ Delete failed: ${response.message}`);
      }
    } catch (error) {
      alert(`❌ Delete error: ${error.message}`);
    } finally {
      setDeletingTenders(prev => {
        const newSet = new Set(prev);
        newSet.delete(tenderId);
        return newSet;
      });
    }
  };

  const openInDrive = (folderUrl) => {
    if (folderUrl) {
      window.open(folderUrl, '_blank');
    } else {
      alert('No Google Drive folder linked to this tender.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle dates like "02-Jan-2026 03:00 PM"
    if (dateString.includes('-') && dateString.includes(':')) {
      return dateString; // Already formatted
    }
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="manage-tenders">
        <div className="loading-section">
          <h2>Loading Tenders from Google Sheets...</h2>
          <div className="loading-spinner">🔄</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-tenders">
      <div className="header-section">
        <div className="header-content">
          <h2>Manage Tenders</h2>
          <p>📊 Tenders stored in Google Sheets • 📁 Documents in Google Drive</p>
        </div>
        <div className="header-actions">
          <button onClick={loadTenders} className="refresh-button">
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {!error && tenders.length === 0 && (
        <div className="empty-state">
          <h3>No tenders found</h3>
          <p>Use the "Search Tenders" section to find and download tenders.</p>
        </div>
      )}

      {tenders.length > 0 && (
        <div className="tenders-section">
          <div className="summary-bar">
            <span className="tender-count">📊 Total: {tenders.length} tenders</span>
            <span className="firebase-status">✅ Google Sheets: Connected</span>
          </div>

          <div className="tender-grid">
            {tenders.map((tender) => (
              <div key={tender.tender_id} className="firebase-tender-card">
                <div className="tender-header">
                  <h4>{tender.title || tender.work_description || 'Untitled Tender'}</h4>
                  <div className="tender-badges">
                    <span className="tender-id-badge">{tender.tender_reference_number || tender.tender_id}</span>
                    <span className={`status-badge ${tender.status}`}>
                      {tender.status || 'pending'}
                    </span>
                  </div>
                </div>

                <div className="tender-info">
                  <div className="info-row">
                    <span className="label">Organization:</span>
                    <span>{tender.organization || tender.organisation_chain || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Location:</span>
                    <span>{tender.location || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Tender Value:</span>
                    <span className="amount">{tender.tender_value || 'N/A'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">EMD Amount:</span>
                    <span className="amount">₹{tender.emd_amount || '0'}</span>
                  </div>
                </div>

                <div className="tender-dates">
                  <div className="date-item">
                    <span className="date-label">Published:</span>
                    <span>{formatDate(tender.published_date)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">Closing:</span>
                    <span>{formatDate(tender.closing_date || tender.bid_submission_end_date)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">Opening:</span>
                    <span>{formatDate(tender.bid_opening_date)}</span>
                  </div>
                </div>

                <div className="firebase-info">
                  <div className="info-row">
                    <span className="label">Uploaded by:</span>
                    <span>{tender.uploaded_by || 'admin'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Uploaded at:</span>
                    <span>{formatDate(tender.uploaded_at)}</span>
                  </div>
                </div>

                <div className="tender-actions">
                  <button
                    className="view-button drive-button"
                    onClick={() => openInDrive(tender.folder_url)}
                    disabled={!tender.folder_url}
                    title={tender.folder_url ? 'Open documents in Google Drive' : 'No Drive folder linked'}
                  >
                    📁 Open in Drive
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDeleteTender(tender.tender_id, tender.title || 'this tender')}
                    disabled={deletingTenders.has(tender.tender_id)}
                  >
                    {deletingTenders.has(tender.tender_id) ?
                      '🗑️ Deleting...' :
                      '🗑️ Delete'
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTenders;
